import { useEffect, useState } from "react";
import {
  Loader2, CheckCircle2, XCircle, MessageSquare, Eye, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getContract, transition } from "../../services/b2bContracts";

/**
 * ContractReviewDialog — Iteration 65
 *
 * Admin-only. The "both modes" decision the user made:
 *   • Approve (with optional inline rate edit captured as diff)
 *   • Reject with a reason
 *   • Send back to the manager with comments (request_changes)
 *
 * Inline-edit UI is intentionally minimal in Phase 1: admin enters
 * a pickup-charge override and a fuel-surcharge override. Anything
 * beyond that → use "Request changes" and let the manager edit.
 */
export default function ContractReviewDialog({ contractId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [inlinePickup, setInlinePickup] = useState("");
  const [inlineFuel, setInlineFuel] = useState("");

  useEffect(() => {
    if (!contractId) { setDoc(null); return; }
    (async () => {
      setLoading(true);
      try {
        const data = await getContract(contractId);
        setDoc(data);
        setInlinePickup(String(data.pickup_charge ?? ""));
        setInlineFuel(String(data.fuel_surcharge_pct ?? ""));
        setComment(""); setReason("");
      } catch {
        toast.error("Could not load contract");
      } finally {
        setLoading(false);
      }
    })();
  }, [contractId]);

  if (!contractId) return null;

  const act = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onClose?.(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.[0]?.msg || "Action failed"));
    } finally { setBusy(false); }
  };

  const approve = () => act("Approved", async () => {
    const inline_edits = {};
    const pNew = Number(inlinePickup);
    const fNew = Number(inlineFuel);
    if (!Number.isNaN(pNew) && pNew !== doc.pickup_charge) inline_edits.pickup_charge = pNew;
    if (!Number.isNaN(fNew) && fNew !== doc.fuel_surcharge_pct) inline_edits.fuel_surcharge_pct = fNew;
    const body = { comment: comment || undefined };
    if (Object.keys(inline_edits).length) body.inline_edits = inline_edits;
    await transition(contractId, "approve", body);
  });

  const reject = () => {
    if (!reason.trim()) { toast.error("Reason is required to reject"); return; }
    return act("Rejected", () => transition(contractId, "reject", { reason, comment }));
  };

  const sendBack = () => {
    if (!comment.trim()) { toast.error("Comment is required when sending back"); return; }
    return act("Sent back to manager", () => transition(contractId, "request-changes", { reason, comment }));
  };

  return (
    <Dialog open={!!contractId} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contract-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-amber-600" />
            Review proposal
          </DialogTitle>
        </DialogHeader>

        {loading || !doc ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p className="text-sm font-semibold text-slate-900">{doc.company_name}</p>
              <p className="text-xs text-slate-500 mt-0.5">{doc.legal_name || "—"} · {doc.gstin || "no GSTIN"}</p>
              <p className="text-xs text-slate-500 mt-1">
                {doc.origin_city || "any city"} · {doc.rate_cards?.length || 0} rate card(s) · {doc.user_ids?.length || 0} user(s) · v{doc.version}
              </p>
            </div>

            <div className="space-y-3">
              <Row label="Pickup charge (INR)">
                <input className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" type="number" min={0}
                       value={inlinePickup} onChange={(e) => setInlinePickup(e.target.value)} data-testid="review-pickup" />
                <span className="text-[11px] text-slate-400 ml-2">was {doc.pickup_charge}</span>
              </Row>
              <Row label="Fuel surcharge %">
                <input className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" type="number" min={0} max={100} step={0.1}
                       value={inlineFuel} onChange={(e) => setInlineFuel(e.target.value)} data-testid="review-fuel" />
                <span className="text-[11px] text-slate-400 ml-2">was {doc.fuel_surcharge_pct}</span>
              </Row>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Comment (visible to manager)</label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                        placeholder="Explain inline edits or feedback…" data-testid="review-comment" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Reason (required if rejecting)</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                        placeholder="e.g. margins too thin, dates conflict with existing contract…" data-testid="review-reason" />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>Close</Button>
              <Button onClick={sendBack} disabled={busy} className="bg-amber-100 text-amber-800 hover:bg-amber-200" data-testid="review-send-back">
                <MessageSquare className="h-4 w-4 mr-1" /> Request changes
              </Button>
              <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="review-reject">
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="review-approve">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</>}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center">
              <ShieldCheck className="h-3 w-3 inline mr-0.5" />
              All review actions are recorded in the contract's audit history.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
      <div className="flex items-center">{children}</div>
    </div>
  );
}
